<template>
  <div class="contact-form-wrapper">
    <div v-if="title || subtitle"
         class="application-form-header">
      <h2 v-if="title"
          class="application-form-title">{{ title }}</h2>
      <p v-if="subtitle"
         class="application-form-subtitle">{{ subtitle }}</p>
    </div>
    <div v-if="showContactMethodSegment"
         class="form-field form-field--contact-method">
      <UISegmentedControl
        v-model="contactMethod"
        :options="contactMethodOptions"
        :aria-label="t('index.contactForm.contactMethodPhone')"
      />
    </div>
    <ContactPhoneForm
      v-if="formPhoneEnabled && contactMethod === 'phone'"
      :show-locality="showLocality"
      :show-region="showRegionFromAccount"
      :show-privacy-checkbox="showPrivacyCheckbox"
      :submit-button-text="submitButtonText"
      :region-options="regionOptions"
      :region-loading="regionLoading"
      :region-iso-code="isoCode"
      :course-id="courseId"
      :default-phone-code="defaultPhoneCode"
      @submit="handleSubmit"
      @error="handleError"
    />
    <ContactEmailForm
      v-else-if="formEmailEnabled && contactMethod === 'email'"
      :show-locality="showLocality"
      :show-region="false"
      :show-privacy-checkbox="showPrivacyCheckbox"
      :submit-button-text="submitButtonText"
      :region-options="regionOptions"
      :region-loading="regionLoading"
      :region-iso-code="isoCode"
      :course-id="courseId"
      @submit="handleSubmit"
      @error="handleError"
    />
    <UISuccessModal v-model="showSuccessModal" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useContactFormChooser } from '@/composables/contact/useContactFormChooser'
import { useContactFormShared } from '@/composables/contact/useContactFormShared'
import { useRegionOptions } from '@/composables/region'
import { useCountryStore } from '@/composables/geo/useCountryStore'
import { useAccountStore } from '@/stores/account'
import { findPhoneCodeByIsoCode } from '@/composables/phone/detect/usePhoneCodeFromCountry'
import ContactPhoneForm from './ContactPhoneForm.vue'
import ContactEmailForm from './ContactEmailForm.vue'
import type { ContactFormData } from '@/composables/contact/contact-form.types'

interface Props {
  title?: string
  subtitle?: string
  showLocality?: boolean
  showRegion?: boolean
  showPrivacyCheckbox?: boolean
  submitButtonText?: string
  /** ID курса — для заявок зі сторінки деталей курса */
  courseId?: string
}

const props = withDefaults(defineProps<Props>(), {
  showLocality: true,
  showRegion: true,
  showPrivacyCheckbox: true,
  submitButtonText: undefined,
  courseId: undefined,
})

const { t } = useI18n()
const showSuccessModal = ref(false)

const {
  contactMethod,
  contactMethodOptions,
  showContactMethodSegment,
  formPhoneEnabled,
  formEmailEnabled,
} = useContactFormChooser()

const shared = useContactFormShared({ showRegion: props.showRegion })

const { countryIsoCode } = useCountryStore()
const isoCode = computed(() => countryIsoCode.value ?? undefined)
const { options: regionOptions, loading: regionLoading } = useRegionOptions(isoCode)

// Show region only when the account config enables it AND the detected country
// actually has regions loaded. BG and other countries without regions never show
// the field even if form_regions_field_enabled is true in the account config.
const showRegionFromAccount = computed(
  () => shared.showRegion.value && regionOptions.value.length > 0,
)

// Phone code priority:
// 1. Detected visitor country (e.g. GB→+44 for UK VPN user on flagship)
// 2. Site's configured country (e.g. by→375, pl→48, am→374)
// 3. Account's default phone code (fallback)
const { defaultPhoneCode: accountPhoneCode } = storeToRefs(useAccountStore())
const appConfig = useAppConfig()
const defaultPhoneCode = computed(() => {
  const detectedIso = countryIsoCode.value
  if (detectedIso) {
    const detectedCode = findPhoneCodeByIsoCode(detectedIso)?.phoneCode
    if (detectedCode) return detectedCode
  }
  const siteIso = (appConfig.site as { defaultCountryIso?: string } | undefined)?.defaultCountryIso
  return (siteIso && findPhoneCodeByIsoCode(siteIso)?.phoneCode) || accountPhoneCode.value
})

const emit = defineEmits<{
  submit: [data: ContactFormData]
  error: [error: Error]
}>()

const handleSubmit = (data: ContactFormData) => {
  emit('submit', data)
  showSuccessModal.value = true
  window.dataLayer?.push({ event: 'zayvka_otpravlena_new' })
  window.gtag?.('event', 'zayvka_otpravlena_new', { send_to: 'G-M4ZQQ35B9L' })
}

const handleError = (error: Error) => {
  emit('error', error)
}
</script>

<style scoped>
.contact-form-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.application-form-header {
  margin-bottom: 16px;
}

.application-form-title {
  font-family: var(--font-inter);
  font-weight: 700;
  font-size: 48px;
  line-height: 1.2;
  color: var(--color-dark-blue);
  margin-bottom: 16px;
}

.application-form-subtitle {
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  line-height: 1.6;
  color: var(--color-grey);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-field--contact-method :deep(.segmented-control) {
  width: 100%;
}

@media (max-width: 480px) {
  .application-form-header {
  text-align: center;
}
.form-field {
  gap: 5px;
}
}
</style>
